import {
    Box,
    Button,
    FormControl,
    FormLabel,
    HStack,
    IconButton,
    Input,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Stack,
    Switch,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useDisclosure
} from "@chakra-ui/react";
import React, { useState } from "react";
import { MdAdd, MdFileDownload, MdMoreHoriz } from "react-icons/md";
import { Link, useParams } from "react-router-dom";
import { AccountAvatar } from "../../components/AccountAvatar";
import { Loader } from "../../components/Loader";
import { LoadMore } from "../../components/LoadMore";
import { Title } from "../../components/Title";
import {
    ClientSignupForm,
    ClientSignupUsingSigalInsurancePolicyForm,
    IClientSignupFormValues,
    IClientSignupUsingSigalInsurancePolicyFormValues
} from "../../forms";
import {
    AccountRole,
    AccountsDocument,
    ClientProfile,
    useAccountsQuery,
    useClientSignupAdminMutation,
    useClientSignupUsingSigalInsurancePolicyMutation,
    useClients2csvMutation
} from "../../graphql";
import { useAccounts } from "../../hooks";
import { formatDateTime } from "../../utils";
import { useDebouncedEffect } from "../Call/filter";

export const AccountsFilter = ({ onChange }) => {
    const [term, setTerm] = useState("");

    const handleChange = () => {
        onChange({
            term
        });
    };

    useDebouncedEffect(() => handleChange(), 1000, [term]);

    return (
        <HStack spacing={ 3 }>
            <Stack>
                <Text fontSize="xs" p={ 0 } m={ 0 } lineHeight={ 0.2 }>
                    Emri
                </Text>
                <Input
                    placeholder="Filtro"
                    size="md"
                    onChange={ (event) => setTerm(event.target.value) }
                />
            </Stack>
        </HStack>
    );
};

const Patients: React.FC = () => {
    const [generatingCSV, setGenerateCSV] = useState(false);
    const [signupUsingPolicy, setSignupUsingPolicy] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const {
        items,
        loading,
        totalCount,
        hasNextPage,
        loadMore,
        refresh
    } = useAccounts(AccountRole.Client, "");

    const [clientSignup] = useClientSignupAdminMutation();
    const [
        clientSignupUsingSigalInsurancePolicy
    ] = useClientSignupUsingSigalInsurancePolicyMutation();

    const [generateCsv] = useClients2csvMutation();

    const handleSignup = async (client: IClientSignupFormValues) => {
        await clientSignup({
            variables: {
                birthday: client.birthday,
                email: client.email,
                languageCode: client.languageCode,
                lastname: client.lastname,
                name: client.name,
                phoneNumber: client.phoneNumber,
                sex: client.sex,
                tierId: parseInt(client.tierId)
            }
        });

        refresh({ search: "" });

        onClose();
    };

    const handleSignupUsingSigalPolicy = async (
        client: IClientSignupUsingSigalInsurancePolicyFormValues
    ) => {
        await clientSignupUsingSigalInsurancePolicy({
            variables: {
                email: client.email,
                insurancePoliceId: parseInt(client.insurancePoliceId),
                insurancePolicyCode: client.insurancePolicyCode,
                birthday: client.birthday,
                languageCode: client.languageCode,
                lastname: client.lastname,
                name: client.name,
                phoneNumber: client.phoneNumber,
                sex: client.sex
            }
        });

        refresh({ search: "" });

        onClose();
    };

    const handleGenerateCsv = async () => {
        setGenerateCSV(true);
        const { data } = await generateCsv();
        const url = data.clients2csv;

        let a = document.createElement("a");
        a.href = url;
        a.download = url.split("/").pop();
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setGenerateCSV(false);
    };

    return (
        <>
            <Modal
                closeOnOverlayClick={ false }
                isOpen={ isOpen }
                onClose={ onClose }
                size="xl"
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Shto një klient</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl display="flex" alignItems="center">
                            <FormLabel htmlFor="use-insurance-policy" mb="0">
                                Përdor policë sigurimi?
                            </FormLabel>
                            <Switch
                                id="use-insurance-policy"
                                defaultChecked={ signupUsingPolicy }
                                onChange={ () => setSignupUsingPolicy(!signupUsingPolicy) }
                            />
                        </FormControl>

                        { signupUsingPolicy ? (
                            <ClientSignupUsingSigalInsurancePolicyForm
                                onSubmit={ (client) => handleSignupUsingSigalPolicy(client) }
                            />
                        ) : (
                            <ClientSignupForm onSubmit={ (client) => handleSignup(client) } />
                        ) }
                    </ModalBody>
                </ModalContent>
            </Modal>
            <>
                <Box
                    borderWidth="1px"
                    borderRadius="lg"
                    overflow="hidden"
                    p={ 2 }
                    background="white"
                >
                    <Title title="Lista e klientëve">
                        <HStack>
                            <AccountsFilter
                                onChange={ (data) => {
                                    refresh({ search: data.term });
                                } }
                            />

                            <Button
                                colorScheme="blue"
                                size="sm"
                                variant="ghost"
                                leftIcon={ <MdAdd /> }
                                onClick={ onOpen }
                            >
                                Shto klient
                            </Button>
                            <Button
                                colorScheme="blue"
                                size="sm"
                                variant="ghost"
                                leftIcon={ <MdFileDownload /> }
                                isLoading={ generatingCSV }
                                onClick={ async () => {
                                    await handleGenerateCsv();
                                } }
                            >
                                Shkarko
                            </Button>
                        </HStack>
                    </Title>
                    { loading ? (
                        <Loader />
                    ) : (
                        <Table variant="striped" size="sm">
                            <Thead>
                                <Tr>
                                    <Th>Emri</Th>
                                    <Th>E-mail</Th>
                                    <Th>Telefoni</Th>
                                    <Th>Paketa</Th>
                                    <Th>Data fillimit</Th>
                                    <Th>Data skadimit</Th>
                                    <Th isNumeric>Data e rregjistrimit</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                { items.map((account) => (
                                    <Tr key={ account.id }>
                                        <Td>
                                            <Link to={ `/account/${account.id}` }>
                                                <AccountAvatar
                                                    account={ account }
                                                    role={ false }
                                                    size="xs"
                                                />
                                            </Link>
                                        </Td>
                                        <Td>{ account.email }</Td>
                                        <Td>{ account.phoneNumber }</Td>
                                        <Td>
                                            { (account.profile as ClientProfile)?.subscription?.tier
                        ?.name
                                                ? (account.profile as ClientProfile)?.subscription?.tier
                            ?.name
                                                : "Asnjë" }
                                        </Td>
                                        <Td>
                                            {
                        (account.profile as ClientProfile)?.subscription
                          ?.startDate
                                            }
                                        </Td>
                                        <Td>
                                            {
                        (account.profile as ClientProfile)?.subscription
                          ?.expirationDate
                                            }
                                        </Td>
                                        <Td isNumeric>{ formatDateTime(account.createdAt) }</Td>
                                    </Tr>
                                )) }
                            </Tbody>
                        </Table>
                    ) }

                    <LoadMore hasNextPage={ hasNextPage } loadMore={ loadMore } />
                </Box>
            </>
        </>
    );
};

export default Patients;
